import { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, inr } from "@/lib/api";
import { Package, Calendar, Heart, User, LogOut, AlertTriangle, CheckCircle2 } from "lucide-react";
import Seo from "@/components/Seo";

export default function Account() {
  const { user, loading, logout } = useAuth();
  const [orders, setOrders] = useState([]);
  const [requests, setRequests] = useState([]);
  const [tab, setTab] = useState("orders");

  useEffect(() => {
    if (!user) return;
    (async () => {
      try {
        const [o, r] = await Promise.all([api.myOrders(), api.serviceRequests.mine()]);
        setOrders(o || []);
        setRequests(r || []);
      } catch (e) { console.error(e); }
    })();
  }, [user]);

  if (loading) return <div className="mx-auto max-w-4xl px-4 py-24 text-center text-[var(--yp-muted)]">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role === "admin") return <Navigate to="/admin" replace />;

  return (
    <div data-testid="account-page" className="mx-auto max-w-6xl px-4 py-10 md:py-14">
      <Seo title="My Account" path="/account" noindex={true} />
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs uppercase tracking-widest text-[var(--yp-muted)]">My Account</div>
          <h1 className="font-serif-display text-3xl md:text-4xl text-[var(--yp-primary)]">Hello, {user.name.split(" ")[0]}</h1>
          <div className="text-sm text-[var(--yp-muted)] mt-1">{user.email}</div>
          {!user.email_verified && (
            <Link data-testid="verify-email-banner" to="/verify-email?send=1&next=/account" className="mt-3 inline-flex items-center gap-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-xl px-3 py-2 hover:bg-amber-100">
              <AlertTriangle size={14} /> Email not verified — you'll need to verify it before placing an order. <span className="font-semibold underline">Verify now</span>
            </Link>
          )}
        </div>
        <button data-testid="btn-logout-account" onClick={logout} className="inline-flex items-center gap-2 border border-[var(--yp-border)] rounded-full px-4 py-2 text-sm hover:border-[var(--yp-primary)]"><LogOut size={14} /> Logout</button>
      </div>

      <div className="mt-8 flex gap-2 border-b border-[var(--yp-border)]">
        {[
          { k: "orders", label: "Orders", icon: Package },
          { k: "requests", label: "Service Requests", icon: Calendar },
          { k: "profile", label: "Profile", icon: User },
        ].map(({ k, label, icon: Icon }) => (
          <button key={k} data-testid={`tab-${k}`} onClick={() => setTab(k)} className={`inline-flex items-center gap-2 px-4 py-3 text-sm border-b-2 ${tab === k ? "border-[var(--yp-primary)] text-[var(--yp-primary)] font-semibold" : "border-transparent text-[var(--yp-muted)] hover:text-[var(--yp-primary)]"}`}>
            <Icon size={16} /> {label}
          </button>
        ))}
      </div>

      <div className="mt-6">
        {tab === "orders" && (
          orders.length === 0 ? <Empty text="No orders yet" cta={{ to: "/shop", label: "Start shopping" }} /> :
          <div className="space-y-4">
            {orders.map((o) => (
              <div key={o.id} className="bg-white border border-[var(--yp-border)] rounded-2xl p-5 flex items-center justify-between">
                <div>
                  <div className="text-xs text-[var(--yp-muted)]">{o.id}</div>
                  <div className="font-medium">{o.items.length} items · {inr(o.total)}</div>
                  <div className="text-xs text-[var(--yp-muted)] mt-1">{o.status}</div>
                </div>
                <Link to={`/order/${o.id}`} className="text-sm text-[var(--yp-primary)] hover:underline">Track →</Link>
              </div>
            ))}
          </div>
        )}
        {tab === "requests" && (
          requests.length === 0 ? <Empty text="No service requests yet" cta={{ to: "/site-visit", label: "Book a visit" }} /> :
          <div className="space-y-4">
            {requests.map((r) => (
              <div key={r.id} className="bg-white border border-[var(--yp-border)] rounded-2xl p-5">
                <div className="text-xs text-[var(--yp-muted)]">{r.id}</div>
                <div className="font-medium">{r.visit_type}</div>
                <div className="text-sm text-[var(--yp-muted)]">{r.date} · {r.time} · {r.city}</div>
                <div className="mt-2"><span className="chip">{r.status}</span></div>
              </div>
            ))}
          </div>
        )}
        {tab === "profile" && (
          <div className="bg-white border border-[var(--yp-border)] rounded-2xl p-6 max-w-lg">
            <Row label="Name" value={user.name} />
            <Row label="Email" value={user.email} />
            <Row label="Email status" value={user.email_verified
              ? <span data-testid="email-status" className="inline-flex items-center gap-1 text-green-700"><CheckCircle2 size={14} /> Verified</span>
              : <span data-testid="email-status" className="inline-flex items-center gap-2 text-amber-700"><AlertTriangle size={14} /> Verification required <Link to="/verify-email?send=1&next=/account" className="underline font-semibold">Verify now</Link></span>} />
            <Row label="Phone" value={user.phone || "—"} />
            <Row label="Member since" value={user.created_at ? new Date(user.created_at).toDateString() : "—"} />
          </div>
        )}
      </div>
    </div>
  );
}

function Row({ label, value }) {
  return <div className="flex justify-between py-2 border-b border-[var(--yp-border)] last:border-0 text-sm"><dt className="text-[var(--yp-muted)]">{label}</dt><dd className="font-medium">{value}</dd></div>;
}
function Empty({ text, cta }) {
  return (
    <div className="text-center py-12 bg-[var(--yp-surface-alt)] rounded-2xl">
      <div className="text-[var(--yp-muted)]">{text}</div>
      {cta && <Link to={cta.to} className="mt-4 inline-block btn-primary rounded-full px-6 py-2.5 text-sm">{cta.label}</Link>}
    </div>
  );
}
